import React, { useState } from "react";
import styles from "../GetInvolved/Modal1.module.css";
import { createInvolvedLead, verifyCaptcha } from "../lib/Store";
import ReCAPTCHA from "react-google-recaptcha";
const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const InvestmentForm = ({
  formType = "Investment Pitch",
  onClose,
  onSuccess,
}) => {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [timeframe, setTimeframe] = useState("within3");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [errors, setErrors] = useState({
    fullName: "",
    email: "",
    timeframe: "",
  });
  const [status, setStatus] = useState({
    type: "",
    message: "",
  });

  const handleCheckbox = (value) => {
    setTimeframe((prev) => (prev === value ? "" : value));
    if (errors.timeframe) {
      setErrors((prev) => ({ ...prev, timeframe: "" }));
    }
  };

  const handleNameChange = (e) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/[^a-zA-Z\s]/g, "");

    setFullName(cleaned);

    let error = "";

    if (!cleaned) {
      error = "Name is required.";
    } else if (cleaned.length < 3) {
      error = "Name must be at least 3 characters.";
    } else if (cleaned.length > 50) {
      error = "Name cannot be more than 50 characters.";
    }

    setErrors((prev) => ({
      ...prev,
      fullName: error,
    }));
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleEmailChange = (e) => {
    const raw = e.target.value || "";
    const value = raw.replace(/\s/g, "");
    setEmail(value);
    let error = "";
    if (!value) {
      error = "Email is required.";
    } else if (value.length < 5) {
      error = "Email must be at least 5 characters.";
    } else if (value.length > 70) {
      error = "Email cannot be more than 70 characters.";
    } else if (!emailRegex.test(value)) {
      error = "Please enter a valid email address.";
    }
    setErrors((prev) => ({
      ...prev,
      email: error,
    }));
  };

  const validate = () => {
    const newErrors = { fullName: "", email: "", timeframe: "" };
    let isValid = true;

    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required.";
      isValid = false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    } else if (!emailPattern.test(email)) {
      newErrors.email = "Please enter a valid email address.";
      isValid = false;
    }

    if (!timeframe) {
      newErrors.timeframe = "Please select when you are ready to invest.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setStatus({ type: "", message: "" });

    if (!validate()) {
      setStatus({
        type: "error",
        message: "",
      });
      return;
    }

    const readyToInvest =
      timeframe === "within3"
        ? "Within 3 months"
        : timeframe === "within6"
        ? "Within 3–6 months"
        : null;

    const resolvedFormType =
      formType === "Investment Pitch" ? "investment_pitch" : formType;

    if (!captchaToken) {
      setCaptchaError("Please verify that you are not a robot.");
      return;
    }

    setLoading(true);
    try {
      const captchaRes = await verifyCaptcha(captchaToken);
      if (captchaRes.message !== "Captcha verified") {
        setCaptchaError("Captcha verification failed.");
        setLoading(false);
        return;
      }
      await createInvolvedLead({
        formType: resolvedFormType,
        fullName,
        email,
        countryCode: null,
        phoneNumber: null,
        businessWebsite: null,
        industries: null,
        readyToInvest,
        anyOtherIndustry: null,
        avgDailyHandledCalls: null,
      });

      setFullName("");
      setEmail("");
      setTimeframe("");
      if (onClose) onClose();
      if (onSuccess) onSuccess(email);
    } catch (err) {
      console.error(err);
      setStatus({
        type: "error",
        message: "Something went wrong while submitting. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <label>
          Your Full Name<span className={styles.requiredStar}>*</span>
        </label>

        <div className={styles.fieldWrapper}>
          <input
            type="text"
            placeholder="Your name"
            value={fullName}
            onChange={handleNameChange}
            className={errors.fullName ? styles.errorInput : ""}
            disabled={loading}
            minLength={3}
            maxLength={50}
          />
          {errors.fullName && (
            <p className={styles.errorText}>{errors.fullName}</p>
          )}
        </div>
        <label>
          Valid Email Address<span className={styles.requiredStar}>*</span>
        </label>

        <div className={styles.fieldWrapper}>
          <input
            type="email"
            placeholder="Enter your email address here"
            value={email}
            onChange={handleEmailChange}
            className={errors.email ? styles.errorInput : ""}
            disabled={loading}
            minLength={5}
            maxLength={70}
            onKeyDown={(e) => {
              if (e.key === " ") {
                e.preventDefault();
              }
            }}
          />
          {errors.email && <p className={styles.errorText}>{errors.email}</p>}
        </div>
        <div className={styles.fieldWrapper2}>
          <div className={styles.mainContainer}>
            <label className={styles.mainLabel}>Ready to Invest?</label>

            <div className={styles.checkboxRow}>
              <div
                className={`${styles.checkDiv} ${
                  errors.timeframe ? styles.errorGroup : ""
                }`}
              >
                <input
                  type="checkbox"
                  id="within3"
                  className={styles.checkbox}
                  checked={timeframe === "within3"}
                  onChange={() => handleCheckbox("within3")}
                  disabled={loading}
                />
                <label htmlFor="within3" className={styles.boxLabel}>
                  Within 3 months
                </label>
              </div>

              <div
                className={`${styles.checkDiv} ${
                  errors.timeframe ? styles.errorGroup : ""
                }`}
              >
                <input
                  type="checkbox"
                  id="within6"
                  className={styles.checkbox}
                  checked={timeframe === "within6"}
                  onChange={() => handleCheckbox("within6")}
                  disabled={loading}
                />
                <label htmlFor="within6" className={styles.boxLabel}>
                  Within 3–6 months
                </label>
              </div>
            </div>
            {errors.timeframe && (
              <p className={styles.errorText}>{errors.timeframe}</p>
            )}
          </div>
        </div>

        {status.message && (
          <p
            className={
              status.type === "success" ? styles.successText : styles.errorText
            }
          >
            {status.message}
          </p>
        )}

        <div className={styles.fieldWrapper}>
          <ReCAPTCHA
            sitekey={SITE_KEY}
            onChange={(token) => {
              setCaptchaToken(token);
              setCaptchaError("");
            }}
          />
          {captchaError && <p className={styles.errorText}>{captchaError}</p>}
        </div>

        <button type="submit" className={styles.callBtn} disabled={loading}>
          <img src="/Svg/shape-btn-form2.svg" alt="btn-shape" />
          <div className={styles.textContent}>
            <h4>{loading ? "Sending..." : "Send to my Inbox"}</h4>
          </div>
        </button>
      </form>
    </div>
  );
};

export default InvestmentForm;
